/**
 * The 44px project tab row — Overview · Drawings · Reviews · Tasks · Activity · People ·
 * Settings, per `design/project/screens/chrome.jsx` and chat1.md §4.
 *
 * **Two different reasons a tab is not here, and they look different** (KLQ-20260819-004 §2):
 *
 * - A surface this caller has no access to is **absent**. `MCK-20260521-004` invariant 2:
 *   *"`none` means not rendered — never a greyed-out control with a tooltip explaining the
 *   denial."* Absence is the whole point; a disabled Tasks tab tells a client that Tasks
 *   exists for somebody, which is exactly what they must not learn.
 * - A surface they *do* have and that this wave has not built yet renders `disabled`, with
 *   the reason in its accessible name. Same call as the left rail's four dead icons: a row
 *   carrying one item reads as a half-finished build.
 *
 * Collapsing the two would leak the first through the second, so this component never
 * decides either — it draws what it is handed. **Which tabs exist for this caller is decided
 * by the server** (`GET /api/v1/projects/{id}/surfaces`) and turned into this list by
 * `features/projects/tabs.ts`.
 *
 * Like `LeftRail`, this reads the catalog directly rather than taking label props: eight
 * labels, one screen family, and threading them through props would put the row's copy
 * somewhere nobody would look for it.
 *
 * **The project's name is in this row as of `KLQ-20260910-001`.** It had a header of its own
 * under the tabs — 24px of padding and a 30px line, on every project screen, saying a word the
 * breadcrumb above already said. On the tasks screen that header sat between the tab row and a
 * filter bar and a toolbar, and the table started below all four. The name is not content; it
 * is *where you are*, which is exactly what a tab row is for, so it moves in beside the tabs
 * and the 54px goes to the list.
 *
 * It stays an `<h1>`. Moving a heading is not the same as demoting one: this is still the
 * page's name, a screen reader still finds the document's title by it, and the tabs are still
 * the `<nav>` beside it rather than around it.
 */

import { Link } from '@tanstack/react-router';

import { useMessages } from '@/lib/i18n';
import { useCenterSelected } from '@/lib/scrollRow';

import styles from './Chrome.module.css';

/**
 * The project's tabs, in chat1.md §4's order.
 *
 * §4 named seven and `register` is the eighth, added 2026-09-10. It is worth a sentence
 * because §6.8 of the same transcript says the opposite: *"Drawing register / Drawing schedule
 * (the master list) → this is essentially our Drawings hierarchy view; we use that term
 * instead."* That line is from the **glossary of words not to introduce**, written 2026-05-21,
 * and it was right about the word. What changed is that the research synthesis pulled the
 * register itself into MVP (`MCK-20260812-011`), and the backend now models it as a surface of
 * its own — `Surface.register` in `authz.py`, with its own access row: an engineer *writes* on
 * drawings and only *reads* the register, because one is where drawings are filed and the
 * other is the list you read, export and hand over. Two access rows is two screens; folding it
 * into the drawings tab would have meant one screen enforcing two of them. Owner's call,
 * 2026-09-10.
 *
 * `transmittals` is the ninth (2026-09-14, `MCK-20260812-010`), after the register it issues
 * from — §6.8 listed *Submittal / Transmittal* as out of MVP, and the research synthesis of
 * 2026-08-12 brought it in; `docs/domain/ui-vocabulary.md` records the correction.
 */
export type ProjectTabKey =
  | 'overview'
  | 'drawings'
  | 'register'
  | 'transmittals'
  | 'reviews'
  | 'tasks'
  | 'activity'
  | 'people'
  | 'settings';

export interface ProjectTab {
  readonly key: ProjectTabKey;
  /**
   * Where the tab goes. **Absent means "permitted but not built yet"** — the tab is drawn
   * disabled. A tab the caller may not reach never reaches this component at all.
   */
  readonly to?: string;
  readonly active: boolean;
}

const LABEL = {
  overview: 'tabs.overview',
  drawings: 'tabs.drawings',
  register: 'tabs.register',
  transmittals: 'tabs.transmittals',
  reviews: 'tabs.reviews',
  tasks: 'tabs.tasks',
  activity: 'tabs.activity',
  people: 'tabs.people',
  settings: 'tabs.settings',
} as const;

export interface ProjectTabsProps {
  readonly tabs: readonly ProjectTab[];
  /** The project's name — the row's leading label, and the page's `<h1>`. */
  readonly name: string;
  /** The project's id, for the `to` params of every built tab. */
  readonly projectId: string;
  /**
   * The phase context, carried across the row (`KLQ-20260827-002`).
   *
   * **Only this one parameter travels.** A tab's own search — the drawings slicer, the tasks
   * filter object, a selected slot — belongs to the screen that owns it, and dragging it onto
   * a different tab would be nonsense. The phase is the exception because it is not the
   * screen's question: it is which stage of the project every screen is answering *about*, and
   * losing it on a tab press is the bug the spec names first.
   */
  readonly phase?: string | undefined;
}

export function ProjectTabs({ tabs, name, projectId, phase }: ProjectTabsProps) {
  const t = useMessages('app');
  const unavailable = t('chrome.unavailable');

  /**
   * The row scrolls on a narrow screen, and nine tabs do not fit a phone — so the active one is
   * pulled into view rather than left wherever `scrollLeft: 0` happens to put it. Owner,
   * 2026-09-21: *"na telefone ta navigacia horizontalna sa vzdy resetne na zaciatok"*. A tab row
   * that shows where you are only while you are on the first three tabs is not a tab row.
   */
  const active = tabs.find((tab) => tab.active)?.key ?? null;
  const { scrollerRef, selectedRef } = useCenterSelected<
    HTMLElement,
    HTMLAnchorElement
  >(active);

  return (
    <div className={styles.tabRow}>
      {/* Outside the `<nav>`: the name is where the reader is, not somewhere they can go. */}
      <h1 className={styles.tabRowName}>{name}</h1>
      <nav ref={scrollerRef} className={styles.tabs} aria-label={t('tabs.label')}>
        {tabs.map((tab) => {
          const label = t(LABEL[tab.key]);
          if (tab.to === undefined) {
            return (
              <button
                key={tab.key}
                type="button"
                disabled
                className={[styles.tab, styles.tabDisabled].join(' ')}
                title={`${label} — ${unavailable}`}
              >
                {label}
                <span className="sr-only">{` — ${unavailable}`}</span>
              </button>
            );
          }
          return (
            <Link
              key={tab.key}
              ref={tab.active ? selectedRef : undefined}
              to={tab.to}
              params={{ projectId }}
              search={phase === undefined ? {} : { phase }}
              className={[styles.tab, tab.active ? styles.tabActive : ''].filter(Boolean).join(' ')}
              aria-current={tab.active ? 'page' : undefined}
            >
              {label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
